/**
 * Bestandskunden-Badge des Ausschreibungs-Boards. Der Server rechnet den Score vor
 * (tri-server docs/tender-existing-customer-score.md) und liefert je Zeile Stufe, Sicherheit und
 * Begründung; hier wird nur gezeigt:
 *
 * - `high` → „Bestandskunde“ (dunkel), `medium` → „Wahrscheinlich Bestandskunde“ (hell);
 * - der Tooltip nennt den passenden Kunden aus dem Kundenstamm, Gesellschaft, Adresse, die
 *   Sicherheit in Prozent und woran der Treffer hängt.
 *
 * Im Seitenpanel (`onFeedback` gesetzt) fragt „Wahrscheinlich Bestandskunde“ in einem Popover
 * zurück: „Ja, ist Bestandskunde“ / „Kein Kunde“. Die Antwort speichert der Server je Mandant, der
 * Score bleibt unberührt; „Kein Kunde“ nimmt das Badge weg und lässt sich zurücknehmen.
 */
import { useEffect, useState } from "react";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { Check, HelpCircle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import type { TenderBoardExistingCustomerMatch, TenderBoardRow } from "../../TenderSearchProfileAPI";

type Reason = TenderBoardExistingCustomerMatch["reasons"][number];

const percent = (value: number): string => `${Math.round(value * 100)} %`;

const reasonText = (reason: Reason, t: TFunction): string | null => {
  switch (reason.kind) {
    case "name":
      return t("tenderBoard.existingCustomer.reason.name", { similarity: percent(reason.similarity) });
    case "buyerAddress":
      return t("tenderBoard.existingCustomer.reason.buyerAddress", { address: reason.address });
    case "objectAddress":
      return t(
        reason.exact
          ? "tenderBoard.existingCustomer.reason.objectAddressExact"
          : "tenderBoard.existingCustomer.reason.objectAddress",
        { addresses: reason.addresses.slice(0, 3).join("; ") },
      );
    case "locality":
      return t("tenderBoard.existingCustomer.reason.locality", { place: reason.place });
    default:
      return null;
  }
};

/** The tooltip lines, top to bottom. Pure, so the wording is testable without hovering. */
export const existingCustomerTooltipLines = (
  match: TenderBoardExistingCustomerMatch,
  t: TFunction,
): string[] =>
  [
    t("tenderBoard.existingCustomer.customer", { name: match.name }),
    match.gesellschaften[0]
      ? t("tenderBoard.existingCustomer.gesellschaft", { name: match.gesellschaften.join(", ") })
      : null,
    match.address ? t("tenderBoard.existingCustomer.address", { address: match.address }) : null,
    t("tenderBoard.existingCustomer.confidence", { value: percent(match.score) }),
    ...match.reasons.map((reason) => reasonText(reason, t)),
  ].filter((line): line is string => !!line);

/** „82 % · Name + Ort" — what the match rests on, in the words of the popover. */
export const existingCustomerMatchSummary = (
  match: TenderBoardExistingCustomerMatch,
  t: TFunction,
): string => {
  const kinds = Array.from(new Set(match.reasons.map((reason) => reason.kind)));
  const basis = kinds.map((kind) => t(`tenderBoard.existingCustomer.basis.${kind}`)).join(" + ");
  return basis ? `${percent(match.score)} · ${basis}` : percent(match.score);
};

type Verdict = "confirmed" | "rejected" | null;

export function ExistingCustomerBadge({
  row,
  onFeedback,
  people = {},
}: {
  row: TenderBoardRow;
  /** Only in the side panel: answer the „Wahrscheinlich“ badge. Rejects with the server's message. */
  onFeedback?: (verdict: Verdict, name: string | null) => Promise<unknown>;
  /** E-mail → name, for „bestätigt von …“. */
  people?: Record<string, string>;
}) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const feedback = row.existingCustomerFeedback ?? null;

  // Inside the side panel the sheet takes Escape away from Radix' own handling (two copies of its
  // layer stack, see TenderDetailPanel), and its focus trap keeps the focus on the trigger — so the
  // popover listens on the document itself while it is open.
  useEffect(() => {
    if (!open) return;
    const close = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", close);
    return () => document.removeEventListener("keydown", close);
  }, [open]);

  const answer = async (verdict: Verdict) => {
    if (!onFeedback) return;
    setBusy(true);
    setError(null);
    try {
      await onFeedback(verdict, row.existingCustomerMatch?.name ?? row.existingCustomer ?? null);
      setOpen(false);
    } catch (err: any) {
      setError(err?.message ?? String(err));
    } finally {
      setBusy(false);
    }
  };

  // „Kein Kunde“ — the badge is gone, but in the panel the verdict stays visible and undoable.
  if (!row.existingCustomer && onFeedback && feedback?.verdict === "rejected" && row.existingCustomerMatch) {
    return (
      <span className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground">
        {t("tenderBoard.existingCustomer.rejected", { name: row.existingCustomerMatch.name })}
        <button
          type="button"
          className="underline underline-offset-2 hover:text-foreground disabled:opacity-50"
          disabled={busy}
          onClick={() => answer(null)}
        >
          {t("tenderBoard.existingCustomer.undo")}
        </button>
      </span>
    );
  }
  if (!row.existingCustomer) return null;
  // An older server sends only the name; that was an exact match, so it counts as sure.
  const match: TenderBoardExistingCustomerMatch = row.existingCustomerMatch ?? {
    level: "high",
    score: 1,
    name: row.existingCustomer,
    gesellschaften: [],
    address: null,
    reasons: [],
  };
  const high = match.level === "high";
  const label = t(high ? "tenderBoard.existingCustomer.badge" : "tenderBoard.existingCustomer.badgeLikely");
  const lines = existingCustomerTooltipLines(match, t);
  const by = feedback?.by ? (people[feedback.by] ?? feedback.by.replace(/@.*$/, "")) : null;

  if (onFeedback && !high) {
    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            data-level={match.level}
            aria-label={`${label}: ${lines.join(" · ")}`}
            className="inline-flex items-center gap-1 rounded border border-foreground/40 bg-muted px-1.5 py-px text-[11px] font-medium text-foreground/80 hover:bg-muted/70"
          >
            <HelpCircle size={11} aria-hidden /> {label}
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-80 space-y-3 text-sm">
          <div className="space-y-1">
            <div className="text-xs uppercase tracking-wide text-muted-foreground">
              {t("tenderBoard.existingCustomer.question")}
            </div>
            <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-sm">
              <dt className="text-muted-foreground">{t("tenderBoard.existingCustomer.customerLabel")}</dt>
              <dd className="font-medium">{match.name}</dd>
              {match.gesellschaften[0] && (
                <>
                  <dt className="text-muted-foreground">{t("tenderBoard.existingCustomer.gesellschaftLabel")}</dt>
                  <dd>{match.gesellschaften.join(", ")}</dd>
                </>
              )}
              <dt className="text-muted-foreground">{t("tenderBoard.existingCustomer.matchLabel")}</dt>
              <dd>{existingCustomerMatchSummary(match, t)}</dd>
            </dl>
          </div>
          {error && <div className="text-xs text-red-700">{error}</div>}
          <div className="flex flex-wrap gap-2">
            <Button size="sm" className="gap-1" disabled={busy} onClick={() => answer("confirmed")}>
              {busy ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
              {t("tenderBoard.existingCustomer.confirm")}
            </Button>
            <Button size="sm" variant="outline" disabled={busy} onClick={() => answer("rejected")}>
              {t("tenderBoard.existingCustomer.reject")}
            </Button>
          </div>
        </PopoverContent>
      </Popover>
    );
  }

  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            tabIndex={0}
            aria-label={`${label}: ${lines.join(" · ")}`}
            data-level={match.level}
            className={
              high
                ? "inline-flex cursor-default items-center gap-1 rounded bg-foreground px-1.5 py-px text-[11px] font-medium text-background"
                : "inline-flex cursor-default items-center gap-1 rounded border border-foreground/40 bg-muted px-1.5 py-px text-[11px] font-medium text-foreground/80"
            }
          >
            <Check size={11} aria-hidden /> {label}
          </span>
        </TooltipTrigger>
        <TooltipContent side="bottom" align="start" className="max-w-sm">
          <div className="space-y-0.5">
            {lines.map((line, i) => (
              <div key={line} className={i === 0 ? "font-semibold" : undefined}>
                {line}
              </div>
            ))}
            {feedback?.verdict === "confirmed" && (
              <div className="text-muted-foreground">
                {by
                  ? t("tenderBoard.existingCustomer.confirmedBy", { name: by })
                  : t("tenderBoard.existingCustomer.confirmedPlain")}
              </div>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
